import { Controller } from "@hotwired/stimulus"

/**
 * Smooth, always-moving progress for planning generation.
 *
 * The server broadcasts discrete percentages via Turbo Stream every few seconds
 * (a decelerating curve from 1% → 95%, then pipeline steps to 100%). Each broadcast
 * replaces the inner HTML of #generation_processing, so this controller reconnects
 * frequently. Rather than easing to each broadcast and then freezing (which reads as
 * "stepping"), we run a continuous animation loop that always creeps toward the latest
 * broadcast value — and gently trickles ahead toward the next phase when it has caught
 * up — so the number counts up smoothly instead of jumping.
 *
 * Continuity across the inner-HTML replacements is kept by persisting the displayed
 * percentage (and the tip clock) on the surviving #generation_processing wrapper.
 */
export default class extends Controller {
  static values = {
    target: Number, // latest broadcast percentage (a floor of real progress)
    tips: Array,
  }
  static targets = ["number", "bar", "tip", "tipText"]

  // Time-constant for the easing (ms). Larger = slower, smoother counting.
  TAU_MS = 2200
  // How long each tip stays on screen (ms). Rotating tip every 20s keeps the
  // long solver wait (8-15 min) informative.
  TIP_INTERVAL_MS = 20000

  connect() {
    this._wrapper = this.element.closest("[data-solver-current-pct]")
    this.displayed = this._wrapper ? parseFloat(this._wrapper.dataset.solverCurrentPct) || 0 : 0
    this.goal = this.targetValue || 0

    this._render(this.displayed)
    this._startCounting()
    this._startTips()
  }

  disconnect() {
    if (this._raf) cancelAnimationFrame(this._raf)
    if (this._tipInterval) clearInterval(this._tipInterval)
    if (this._wrapper) this._wrapper.dataset.solverCurrentPct = this.displayed
  }

  // ---- Continuous progress smoothing -------------------------------------

  _startCounting() {
    if (this._raf) cancelAnimationFrame(this._raf)
    let last = performance.now()

    const tick = (now) => {
      const dt = Math.min(now - last, 250) // clamp big gaps (tab was backgrounded)
      last = now

      let aim = this.goal
      // Caught up and still waiting for the next broadcast? Trickle a little
      // toward the next phase so the bar never sits frozen — but never reach 99
      // until the server actually says so.
      if (this.displayed >= this.goal - 0.4 && this.goal < 99) {
        aim = Math.min(this.goal + (99 - this.goal) * 0.18, 99)
      }

      const factor = 1 - Math.exp(-dt / this.TAU_MS)
      const next = this.displayed + (aim - this.displayed) * factor
      this.displayed = Math.max(this.displayed, next) // monotonic — never go backwards

      this._render(this.displayed)
      this._raf = requestAnimationFrame(tick)
    }

    this._raf = requestAnimationFrame(tick)
  }

  _render(pct) {
    const clamped = Math.max(0, Math.min(pct, 100))
    if (this.hasNumberTarget) this.numberTarget.textContent = Math.round(clamped)
    if (this.hasBarTarget) this.barTarget.style.width = `${clamped.toFixed(1)}%`
  }

  // ---- Rotating tips (one per minute) ------------------------------------

  _startTips() {
    const tips = this.tipsValue
    if (!this.hasTipTextTarget || !Array.isArray(tips) || tips.length === 0) return

    // Anchor the tip clock on the surviving wrapper so rotation continues across
    // the frequent inner-HTML replacements instead of restarting every broadcast.
    let tipStart = this._wrapper ? parseInt(this._wrapper.dataset.solverTipStart, 10) : NaN
    if (!Number.isFinite(tipStart)) {
      tipStart = Date.now()
      if (this._wrapper) this._wrapper.dataset.solverTipStart = tipStart
    }

    const indexFor = () => Math.floor((Date.now() - tipStart) / this.TIP_INTERVAL_MS) % tips.length

    this._tipIndex = indexFor()
    this.tipTextTarget.textContent = tips[this._tipIndex]

    this._tipInterval = setInterval(() => {
      const idx = indexFor()
      if (idx === this._tipIndex) return
      this._tipIndex = idx
      if (this.hasTipTarget) this.tipTarget.classList.add("is-swapping")
      setTimeout(() => {
        this.tipTextTarget.textContent = tips[idx]
        if (this.hasTipTarget) this.tipTarget.classList.remove("is-swapping")
      }, 280)
    }, 1000)
  }
}
